// The Hiring exports: the round's candidates as a CSV, and one candidate as an Excel
// workbook with a sheet per tab of the Analysis modal. A CSV cannot hold sheets.
//
// The workbook carries the team's private notes and the integrity signals, so it is
// for the team. Its Explanation sheet is the part written for a candidate who asks how
// AI was used (L-7): that is what to pass on, not the whole file.

import * as XLSX from 'xlsx';
import type {
  HiringCandidateView,
  HiringRoundView,
  HiringTranscriptView
} from '../../../../api/hiring';
import { levelName, seconds, statusLabel, when } from './hiringParts';

type Cell = string | number;
type Row = Cell[];

const fileName = (...parts: string[]) =>
  parts
    .join(' ')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .toLowerCase();

// A spreadsheet runs a cell starting with one of these as a formula, and these cells
// hold what candidates typed.
const csvCell = (value: Cell): string => {
  const text = String(value);
  const safe = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

const saveCsv = (rows: Row[], name: string) => {
  const csv = rows.map((row) => row.map(csvCell).join(',')).join('\r\n');
  // The byte order mark is what makes Excel read names with accents correctly.
  const url = window.URL.createObjectURL(new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `${name}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/** The Assessment tab as it stands: a row per capability, with its options and the right one. */
export const exportAssessmentCsv = (round: HiringRoundView, items: HiringRoundView['items']): void => {
  const optionCount = Math.max(0, ...items.map((item) => item.quiz.options.length));
  const rows: Row[] = [
    [
      'Number',
      'Capability',
      'Roleplay Situation',
      'Quiz Question',
      ...Array.from({ length: optionCount }, (_, i) => `Option ${i + 1}`),
      'Correct Answer'
    ],
    ...items.map((item, index) => [
      `${index + 1}/${items.length}`,
      item.capabilityName,
      item.roleplay,
      item.quiz.question,
      ...Array.from({ length: optionCount }, (_, i) => item.quiz.options[i] ?? ''),
      item.quiz.options[item.quiz.correct] ?? ''
    ])
  ];
  saveCsv(rows, fileName(round.name, 'assessment'));
};

/** The Activity tab as it stands, newest first. The caller words the rows, as the screen does. */
export const exportActivityCsv = (round: HiringRoundView, entries: Row[]): void =>
  saveCsv([['Date', 'Who', 'What Happened', 'Candidate', 'Reason'], ...entries], fileName(round.name, 'activity'));

/** The Candidates tab as it stands, in the order on screen, without the buttons. */
export const exportRoundCsv = (
  round: HiringRoundView,
  capabilities: Array<{ id: string; name: string }>,
  candidates: HiringCandidateView[]
): void => {
  const rows: Row[] = [
    ['Rank', 'Name', 'Status', 'Completion', ...capabilities.map((c) => c.name)],
    ...candidates.map((candidate, index) => {
      const levels = new Map(candidate.capabilities.map((c) => [c.capabilityId, c.level]));
      return [
        index + 1,
        candidate.name,
        statusLabel(candidate.status),
        `${candidate.progress}%`,
        ...capabilities.map((c) => levelName(levels.get(c.id) ?? null))
      ];
    })
  ];
  saveCsv(rows, fileName(round.name, 'candidates'));
};

const mode = (inputMode: 'text' | 'voice' | null | undefined) =>
  inputMode === 'voice' ? 'Voice' : inputMode === 'text' ? 'Typed' : '';

const capabilitiesSheet = (candidate: HiringCandidateView): Row[] => [
  [
    'Capability',
    'Level',
    'Roleplay Analysis',
    'Attempts Used',
    'Typed / Voice',
    'Copy & Pastes',
    'Dropoffs',
    'CV Analysis',
    'Quiz Question'
  ],
  ...candidate.capabilities.map((c) => [
    c.capabilityName,
    levelName(c.level),
    c.analysis,
    c.attemptsUsed,
    mode(c.inputMode),
    c.signals.pasteCount + c.signals.copyCount,
    c.signals.tabSwitches,
    c.cvAnalysis,
    c.quiz.passed === null ? '' : c.quiz.passed ? 'Correct' : 'Incorrect'
  ])
];

const notesSheet = (candidate: HiringCandidateView): Row[] => [
  ['Added By', 'Date', 'Edited', 'Note'],
  ...candidate.notes.map((note) => [note.addedByName, when(note.addedAt), when(note.editedAt), note.text])
];

const transcriptSheet = (transcript: HiringTranscriptView): Row[] => {
  const rows: Row[] = [
    [
      'Capability',
      'From',
      'Text',
      'Question (No Attempt Used)',
      'Typed / Voice',
      'Replied In',
      'Pasted',
      'Copied',
      'Left The Tab',
      'Correct Answer'
    ]
  ];
  transcript.capabilities.forEach((c) => {
    rows.push([c.capabilityName, 'Elliott', c.situation]);
    c.exchanges.forEach((e) => {
      const candidateTurn = e.from === 'candidate';
      rows.push([
        c.capabilityName,
        candidateTurn ? 'Candidate' : 'Elliott',
        e.text,
        e.clarification ? 'Yes' : '',
        candidateTurn ? mode(e.inputMode) : '',
        candidateTurn && e.responseSeconds !== null ? seconds(e.responseSeconds) : '',
        candidateTurn ? e.pasteCount : '',
        candidateTurn ? e.copyCount : '',
        candidateTurn ? e.tabSwitches : ''
      ]);
    });
    rows.push([c.capabilityName, 'Quiz Question', c.quiz.question]);
    rows.push([
      c.capabilityName,
      'Candidate',
      c.quiz.answerIndex === null ? 'Not answered' : c.quiz.options[c.quiz.answerIndex] ?? '',
      '',
      '',
      '',
      '',
      '',
      '',
      c.quiz.options[c.quiz.correct] ?? ''
    ]);
  });
  return rows;
};

const detailsSheet = (candidate: HiringCandidateView, round: HiringRoundView): Row[] => [
  ['Detail', 'Value'],
  ['Name', candidate.name],
  ['Email', candidate.email],
  ['Status', statusLabel(candidate.status)],
  [
    'Country of Residence',
    candidate.residence ? [candidate.residence.country, candidate.residence.state].filter(Boolean).join('-') : ''
  ],
  ...Object.entries(candidate.answers).map(
    ([key, value]): Row => [round.fields.find((f) => f.key === key)?.label || key, value]
  ),
  ['LinkedIn', candidate.linkedinUrl],
  ['CV', candidate.hasCv ? candidate.cvFileName : ''],
  ['Company Email', candidate.workEmail]
];

const timelineSheet = (candidate: HiringCandidateView): Row[] => [
  ['Event', 'Date'],
  ['Invited', when(candidate.invitedAt)],
  ['Notice Acknowledged', candidate.noticeAt ? when(candidate.noticeAt) : ''],
  ['Started', candidate.startedAt ? when(candidate.startedAt) : ''],
  ['Completed', candidate.completedAt ? when(candidate.completedAt) : ''],
  ['Removed', candidate.removedAt ? when(candidate.removedAt) : ''],
  ['Hired', candidate.hiredAt ? when(candidate.hiredAt) : ''],
  ['Data Retention', `${candidate.retention} after the round closes`]
];

/** Any nested record as Field and Value rows, so nothing the server sends is dropped. */
const flatten = (value: unknown, path: string, rows: Row[]): void => {
  if (Array.isArray(value)) {
    value.forEach((item, index) => flatten(item, `${path} ${index + 1}`, rows));
  } else if (value && typeof value === 'object') {
    Object.entries(value as Record<string, unknown>).forEach(([key, item]) => {
      const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
      flatten(item, path ? `${path} / ${label}` : label, rows);
    });
  } else {
    rows.push([path, value === null || value === undefined ? '' : String(value)]);
  }
};

/** One candidate, a sheet per tab of the Analysis modal, then the L-7 explanation. */
export const exportCandidateWorkbook = (
  candidate: HiringCandidateView,
  round: HiringRoundView,
  transcript: HiringTranscriptView,
  explanation: Record<string, unknown>
): void => {
  const explanationRows: Row[] = [['Field', 'Value']];
  flatten(explanation, '', explanationRows);
  const sheets: Array<[string, Row[]]> = [
    ['Capabilities', capabilitiesSheet(candidate)],
    ['Notes', notesSheet(candidate)],
    ['Transcript', transcriptSheet(transcript)],
    ['Details', detailsSheet(candidate, round)],
    ['Timeline', timelineSheet(candidate)],
    ['Explanation', explanationRows]
  ];
  const workbook = XLSX.utils.book_new();
  sheets.forEach(([name, rows]) => {
    const sheet = XLSX.utils.aoa_to_sheet(rows);
    // Wide enough to read, capped so a long analysis doesn't push everything off screen.
    sheet['!cols'] = rows[0].map((_, column) => ({
      wch: Math.min(60, Math.max(12, ...rows.map((row) => String(row[column] ?? '').length)))
    }));
    XLSX.utils.book_append_sheet(workbook, sheet, name);
  });
  XLSX.writeFile(workbook, `${fileName(candidate.name, round.name)}.xlsx`);
};
